import { useState, useEffect, useMemo } from "react";
import { supabase } from "@/integrations/supabase/client";
import { ExternalRoundStats, CourseStatsFilters } from "./useCourseStats";

export const useSupervisedCourseStats = (traineeUserId?: string) => {
  const [stats, setStats] = useState<ExternalRoundStats[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  
  // Default: show all data, with option to filter by current year
  const currentYear = new Date().getFullYear();
  const [filters, setFilters] = useState<CourseStatsFilters>({
    holesFilter: "18",
    courseFilter: "all",
    dateFrom: new Date(currentYear, 0, 1),
    dateTo: new Date(),
    showAllRounds: true, // Show all rounds by default
  });

  // Fetch stats for trainee
  const fetchStats = async () => {
    if (!traineeUserId) {
      setStats([]);
      return;
    }

    try {
      setIsLoading(true);
      
      const { data, error } = await supabase
        .from("external_round_stats")
        .select("*")
        .eq("user_id", traineeUserId)
        .order("round_date", { ascending: false });

      if (error) {
        console.error("Error fetching trainee stats:", error);
        return;
      }

      setStats((data as ExternalRoundStats[]) || []);
    } catch (error) {
      console.error("Error:", error);
    } finally {
      setIsLoading(false);
    }
  };

  // Get unique courses for filter - filtered by holes selection
  const courses = useMemo(() => {
    const uniqueCourses = new Set<string>();
    stats.forEach(s => {
      if (s.course_name) {
        // Only include courses that match the holes filter
        if (filters.holesFilter === "all" || s.holes_played === parseInt(filters.holesFilter)) {
          uniqueCourses.add(s.course_name);
        }
      }
    });
    return Array.from(uniqueCourses).sort();
  }, [stats, filters.holesFilter]);

  // Apply filters
  const filteredStats = useMemo(() => {
    return stats.filter(stat => {
      // Holes filter
      if (filters.holesFilter !== "all") {
        const holesNum = parseInt(filters.holesFilter);
        if (stat.holes_played !== holesNum) return false;
      }

      // Course filter
      if (filters.courseFilter !== "all" && stat.course_name !== filters.courseFilter) {
        return false;
      }

      // Date filters (only apply if not showing all rounds)
      if (!filters.showAllRounds) {
        const roundDate = new Date(stat.round_date);
        if (filters.dateFrom && roundDate < filters.dateFrom) return false;
        if (filters.dateTo && roundDate > filters.dateTo) return false;
      }

      return true;
    });
  }, [stats, filters]);

  useEffect(() => {
    fetchStats();
  }, [traineeUserId]);

  const refetch = () => {
    fetchStats();
  };

  return {
    stats,
    filteredStats,
    isLoading,
    filters,
    setFilters,
    courses,
    refetch,
  };
};
